// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngSticky from "@keller/App/Components/XngSticky";
import { DivWrapper } from "@keller/XDL/constants";
import { mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import React, { FC, PropsWithChildren, useMemo, useState } from "react";
import { css } from "styled-components";

interface CartPageTemplateProps {
    children: React.ReactNode;
    extendedStyles?: CartPageTemplateStyles;
}

interface Props {
    fulfillmentMethodSection: React.FC<any>;
    MainSection: React.FC<any>;
    HeaderSection: React.FC<any>;
    CartTotalSection: React.FC<any>;
    ActionsSection: React.FC<any>;
}

interface CartPageTemplateContextProps {
    styles: CartPageTemplateStyles;
}

export interface CartPageTemplateStyles {
    container?: InjectableCss;
    headerSection?: InjectableCss;
    fulfillmentMethodSection?: InjectableCss;
    mainSection?: InjectableCss;
    totalSection?: InjectableCss;
    actionsSection?: InjectableCss;
}

const CartPageTemplateContext = React.createContext<CartPageTemplateContextProps>({
    styles: {},
});

export const useCartPageTemplateContext = () => {
    const context = React.useContext(CartPageTemplateContext);
    if (!context) {
        throw new Error("CartPageTemplate compound components cannot be rendered outside CartPageTemplate");
    }
    return context;
};

export const cartPageTemplateBreakpoint = 760;
export const cartPageTemplateStyles: CartPageTemplateStyles = {};

const CartPageTemplate: FC<CartPageTemplateProps> & Props = ({ children, extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(cartPageTemplateStyles, extendedStyles));
    const value = useMemo(() => ({ styles }), [styles]);
    return (
        <CartPageTemplateContext.Provider value={value}>
            <StyledWrapper {...styles.container} data-test-selector="CartPageTemplate">
                {children}
            </StyledWrapper>
        </CartPageTemplateContext.Provider>
    );
};

const HeaderSection: FC<PropsWithChildren> = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.headerSection}>{children}</StyledWrapper>;
};

const fulfillmentMethodSection: FC<PropsWithChildren> = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.fulfillmentMethodSection}>{children}</StyledWrapper>;
};

const MainSection: FC<PropsWithChildren> = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.mainSection}>{children}</StyledWrapper>;
};

const CartTotalSection: FC<PropsWithChildren> = ({ children, ...props }) => {
    const { styles } = useCartPageTemplateContext();

    return (
        <StyledWrapper {...styles.totalSection} {...props}>
            <XngSticky>{children}</XngSticky>
        </StyledWrapper>
    );
};

const ActionsSection: FC<PropsWithChildren> = ({ children, ...props }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.actionsSection}>{children}</StyledWrapper>;
};

CartPageTemplate.HeaderSection = HeaderSection;
CartPageTemplate.fulfillmentMethodSection = fulfillmentMethodSection;
CartPageTemplate.MainSection = MainSection;
CartPageTemplate.CartTotalSection = CartTotalSection;
CartPageTemplate.ActionsSection = ActionsSection;

export default CartPageTemplate;
